import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from './PageHeader';
import Breadcrumbs from './Breadcrumbs';

export default function TermsOfService({ t, lang, setLang }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isGerman = lang === 'de';

  const breadcrumbItems = [
    { label: t('breadcrumb_home') || 'Home', path: '/' },
    { label: isGerman ? 'Nutzungsbedingungen' : 'Terms of Service', path: null },
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <PageHeader lang={lang} setLang={setLang} t={t} />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Breadcrumbs items={breadcrumbItems} />

        <div className="mt-8 space-y-8">
          <h1 className="text-3xl font-bold text-white mb-6">
            {isGerman ? 'Nutzungsbedingungen' : 'Terms of Service'}
          </h1>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '1. Geltungsbereich' : '1. Scope'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Diese Nutzungsbedingungen gelten für die Nutzung von Clansight und alle damit verbundenen Dienste. Mit der Nutzung der Website erklären sich die Nutzer mit diesen Bedingungen einverstanden.'
                : 'These Terms of Service apply to the use of Clansight and all related services. By using the website, users agree to these terms.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '2. Beschreibung des Dienstes' : '2. Description of Service'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Clansight ist ein Community-Tool für Clash of Clans Clans. Funktionen können umfassen: Clan-Statistiken, Kriegs-Tracking, Spielerleistungen, Ranglisten, Discord-Integration. Einige Daten können von externen APIs stammen.'
                : 'Clansight is a community tool for Clash of Clans clans. Features may include: clan statistics, war tracking, player performance, rankings, Discord integration. Some data may come from external APIs.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '3. Keine Verbindung zu Drittanbietern' : '3. No Affiliation'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Clansight steht in keiner Verbindung zu: Supercell, Clash of Clans, Discord. Alle Markenzeichen gehören ihren jeweiligen Eigentümern.'
                : 'Clansight is not affiliated with: Supercell, Clash of Clans, Discord. All trademarks belong to their respective owners.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '4. Registrierung' : '4. Registration'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Die Registrierung erfolgt über Discord-Authentifizierung. Der Dienst ist hauptsächlich für Clan-Leaders und Co-Leaders gedacht. Der Betreiber kann Accounts bei Verstoss gegen diese Bedingungen, Missbrauch oder Sicherheitsrisiken einschränken oder entfernen.'
                : 'Registration is performed through Discord authentication. The service is mainly intended for clan leaders and co-leaders. The operator may restrict or remove accounts if these terms are violated, the service is misused, or security risks occur.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '5. Datenrichtigkeit und Verfügbarkeit' : '5. Data Accuracy and Availability'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Informationen basieren teilweise auf externen Datenquellen. Es besteht keine Garantie für: Richtigkeit, Vollständigkeit, Verfügbarkeit, dauerhaften Betrieb.'
                : 'Information is partly based on external data sources. No guarantee is given regarding: correctness, completeness, availability, continuous operation.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '6. Nutzerpflichten' : '6. User Responsibilities'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Nutzer stimmen zu: keine falschen Informationen anzugeben, keine Sicherheitsmassnahmen zu umgehen, den Dienst nicht zu missbrauchen, keine schädlichen automatisierten Systeme zu verwenden, Discord- und Clash of Clans-Regeln zu befolgen.'
                : 'Users agree: not to provide false information, not to bypass security measures, not to misuse the service, not to use harmful automated systems, to follow Discord and Clash of Clans rules.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '7. Kostenpflichtige Funktionen' : '7. Paid Features'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Clansight kann zukünftig kostenpflichtige Funktionen einführen. Preise und Funktionen werden vorher kommuniziert. Zahlungsmethoden können direkt oder über externe Zahlungsanbieter angeboten werden.'
                : 'Clansight may introduce paid features in the future. Prices and features will be communicated before use. Payment methods may be offered directly or through external payment providers.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '8. Zahlung und Zugang' : '8. Payment and Access'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Kostenpflichtiger Zugang wird nach erfolgreicher Zahlung aktiviert. Der Betreiber speichert keine vollständigen Zahlungsinformationen.'
                : 'Paid access is activated after successful payment. The operator does not store complete payment information.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '9. Kündigung' : '9. Cancellation'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Nutzer können die Nutzung jederzeit beenden. Kostenpflichtige Dienste unterliegen den vereinbarten Bedingungen und Laufzeiten.'
                : 'Users may stop using the service at any time. Paid services are subject to the agreed terms and periods.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '10. Rückerstattungen' : '10. Refunds'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Zahlungen sind grundsätzlich nicht rückerstattungsfähig, es sei denn das geltende Recht erfordert dies anders. Der Betreiber kann bei technischen Problemen freiwillig Lösungen anbieten.'
                : 'Payments are generally non-refundable unless mandatory law requires otherwise. The operator may voluntarily provide solutions in case of technical problems.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '11. Änderungen oder Einstellung' : '11. Changes or Discontinuation'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Der Betreiber kann Teile des Dienstes ändern oder einstellen. Nutzer werden soweit möglich informiert.'
                : 'The operator may modify or discontinue parts of the service. Users will be informed where reasonably possible.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '12. Haftung' : '12. Liability'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Die Nutzung erfolgt auf eigenes Risiko. Der Betreiber haftet nur nach geltendem Recht. Keine Haftung für: Ausfälle, externe Dienstefehler, API-Änderungen, Datenverluste, Missbrauch. Haftung für vorsätzliches Fehlverhalten oder grobe Fahrlässigkeit bleibt unberührt.'
                : 'The service is used at the user\'s own risk. The operator is liable only according to applicable law. No liability for: downtime, external service failures, API changes, data loss, misuse. Liability for intentional misconduct or gross negligence remains unaffected.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '13. Drittanbieter-Dienste' : '13. Third Party Services'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Der Dienst kann folgende Anbieter nutzen: Discord, Supabase, Clash of Clans API / Supercell. Diese Anbieter haben eigene Nutzungsbedingungen und Datenschutzrichtlinien.'
                : 'The service may use: Discord, Supabase, Clash of Clans API / Supercell. These providers have their own terms and privacy policies.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '14. Anwendbares Recht' : '14. Applicable Law'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Diese Bedingungen unterliegen Schweizer Recht. Gerichtsstand ist der Sitz des Betreibers, soweit rechtlich zulässig.'
                : 'These terms are governed by Swiss law. The place of jurisdiction is the operator\'s registered location where legally permitted.'}
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-blue-400">
              {isGerman ? '15. Salvatorische Klausel' : '15. Severability'}
            </h2>
            <p className="text-gray-300 leading-relaxed">
              {isGerman
                ? 'Sollten einzelne Bestimmungen unwirksam sein oder werden, bleiben die übrigen Bestimmungen gültig.'
                : 'If any provision becomes invalid, the remaining provisions remain valid.'}
            </p>
          </section>

          <div className="mt-8 pt-8 border-t border-gray-700">
            <p className="text-sm text-gray-400">
              {isGerman ? 'Stand: Juni 2026' : 'Last updated: June 2026'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
